import { motion, useMotionValue, useTransform, animate, useInView } from "framer-motion";
import { useEffect, useRef } from "react";

interface StatCardProps {
  number: number;
  suffix?: string;
  label: string;
  delay?: number;
  duration?: number;
  className?: string;
  numberClassName?: string;
  labelClassName?: string;
  animation?: {
    initial?: any;
    whileInView?: any;
    transition?: any;
  };
}

const StatCard = ({
  number,
  suffix = "",
  label,
  delay = 0,
  duration = 2,
  className = "",
  numberClassName = "",
  labelClassName = "",
  animation = {
    initial: { opacity: 0, y: 20 },
    whileInView: { opacity: 1, y: 0 },
    transition: { duration: 0.6, delay }
  }
}: StatCardProps) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });
  const count = useMotionValue(0);
  const rounded = useTransform(count, (latest) => 
    Math.round(latest).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")
  );

  useEffect(() => {
    if (isInView) {
      const controls = animate(count, number, {
        duration,
        delay: delay * 0.3,
        ease: "easeOut",
      });

      return controls.stop;
    }
  }, [isInView, count, number, delay, duration]);

  return (
    <motion.div
      ref={ref}
      className={`text-center ${className}`}
      initial={animation.initial}
      whileInView={animation.whileInView}
      transition={animation.transition}
      viewport={{ once: true }}
    >
      <div className={`text-4xl md:text-5xl font-bold text-teal-400 mb-2 ${numberClassName}`}>
        <motion.span>{rounded}</motion.span>
        {suffix && <span className="text-teal-400">{suffix}</span>}
      </div>
      <p className={`text-slate-400 ${labelClassName}`}>
        {label}
      </p>
    </motion.div>
  );
};

export default StatCard;
